import React, { useContext, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { includes, map, pluck } from "ramda";
import { notBlank } from "../../../util/ext";
import { useMediaTable } from "../../common/use-mediaTable";
import { Link, useRouteMatch, useHistory } from "react-router-dom";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import { faCircleMinus } from "@fortawesome/pro-regular-svg-icons";
import { useDeleteModuleItems } from "../../modules/moduleServices";

import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Popover from "@material-ui/core/Popover";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";

import AnthologyCard from "./AnthologyCard";
import CopyIcon from "../../common/CopyIcon";
import ListSummaryCard from "../../common/ListSummaryCard";
import StatusTableCell from "../../common/StatusTableCell";
import {
  AlertContext,
  AlertSeverity,
} from "../../../components/alert/AlertProvider";
import { useIsAdmin } from "../../../auth/authUtils";
import OwnersList from "../../modules/browse/OwnersList";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  contentCellHeader: {
    width: "33%",
    background: "white",
    paddingLeft: theme.spacing(0),
  },

  contentCell: {
    width: "33%",
    paddingRight: theme.spacing(3),
  },

  cell: {
    minWidth: 100,
    maxWidth: 150,
    padding: 0,
    background: "white",
  },

  menu: {
    // marginLeft: 15,
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const RemoveButton = React.forwardRef(({ anthologyId, moduleId }, ref) => {
  const alert = useContext(AlertContext);
  const deleteModuleItems = useDeleteModuleItems();
  const handleRemoveFromModule = async event => {
    event.stopPropagation();
    try {
      await deleteModuleItems.mutateAsync({
        id: moduleId,
        ids: [anthologyId],
      });
    } catch (err) {
      const messages =
        err?.body && notBlank(err.body.errors)
          ? pluck("message", err.body.errors)
          : [err.message];
      alert.showMessage(messages[0], AlertSeverity.error);
    }
  };

  return (
    <Tooltip ref={ref} title="Remove Anthology From Module">
      <IconButton edge="end" onClick={handleRemoveFromModule}>
        {deleteModuleItems.isLoading ? (
          <CircularProgress size={15} />
        ) : (
          <FontAwesomeIcon variant="delete" icon={faCircleMinus} />
        )}
      </IconButton>
    </Tooltip>
  );
});

const AnthologyTable = props => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const isAdmin = useIsAdmin();

  const {
    searchState,
    anthologyList,
    page,
    onPageChange,
    total = 0,
    onSelectChange,
    orderBy = "name",
    order = "asc",
    onSortChange,
    onScrollVisible,
  } = props;

  const classes = useStyles();
  const {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
    handleSelectAllClick,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, tableContainerEl]);

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Anthology">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" align="center">
                <Checkbox
                  color="primary"
                  indeterminate={
                    state.selected.length > 0 &&
                    state.selected.length < anthologyList.length
                  }
                  checked={
                    state.selected.length > 0 &&
                    state.selected.length === anthologyList.length
                  }
                  onChange={event =>
                    handleSelectAllClick(event.target, anthologyList)
                  }
                  inputProps={{ "aria-label": "Select" }}
                />
              </TableCell>

              <TableCell
                key="anthology"
                align="left"
                className={classes.contentCellHeader}
              >
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={order}
                  onClick={() => handleRequestSort("name")}
                >
                  Title
                  {orderBy === "name" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="date-updated"
                align="left"
                classes={{ root: classes.cell }}
              >
                <TableSortLabel
                  active={orderBy === "recordDate"}
                  direction={order}
                  onClick={() => handleRequestSort("recordDate")}
                >
                  Last Update
                  {orderBy === "auditUpdated" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              {isAdmin && (
                <TableCell key="organization">Library/Organization</TableCell>
              )}

              <TableCell key="anthology-status">Status</TableCell>

              <TableCell
                key="modules"
                align="left"
                classes={{ root: classes.cell }}
                style={{ paddingLeft: 12 }}
              >
                {/* <TableSortLabel
                  active={orderBy === "modules"}
                  direction={order}
                  onClick={() => handleRequestSort("modules")}
                > */}
                Modules
                {/* {orderBy === "modules" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel> */}
              </TableCell>

              <TableCell
                key="anthology-id"
                align="center"
                style={{ width: 75 }}
              >
                Anthology ID
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {anthologyList.map(anthology => (
              <TableRow
                onClick={() => handleRowClick(anthology)}
                hover
                tabIndex={-1}
                key={anthology.id}
                role="checkbox"
                aria-checked={includes(anthology.id, state.selected)}
                selected={includes(anthology.id, state.selected)}
              >
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    color="primary"
                    checked={includes(anthology.id, state.selected)}
                    inputProps={{
                      "aria-labelledby": "content-checkbox-" + anthology.id,
                    }}
                  />
                </TableCell>

                <TableCell
                  key={"title:" + anthology.id}
                  align="left"
                  className={classes.contentCell}
                >
                  <Link to={`${url}/${anthology.id}`}>
                    <AnthologyCard anthology={anthology} />
                  </Link>
                </TableCell>

                <TableCell
                  key={"date-updated:" + anthology.id}
                  align="left"
                  className={classes.addedCell}
                >
                  {new Date(anthology.auditUpdated).toLocaleDateString()}
                </TableCell>

                {isAdmin && (
                  <TableCell key={"organization: " + anthology.id}>
                    <OwnersList media={anthology} searchState={searchState} />
                  </TableCell>
                )}

                <StatusTableCell
                  key={"anthology-status: " + anthology.id}
                  status={anthology.status}
                />

                <TableCell key={"modules:" + anthology.id} align="left">
                  <ListSummaryCard
                    data={anthology.ancestorModules}
                    renderPopover={popoverProps => (
                      <Popover
                        onClick={e => e.stopPropagation()}
                        open={popoverProps.open}
                        anchorEl={popoverProps.anchorEl}
                        onClose={popoverProps.handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        classes={{
                          paper: classes.menu,
                        }}
                      >
                        <List
                          id={`modules-for-${anthology.id}`}
                          color="inherit"
                        >
                          {map(
                            module => (
                              <MenuItem
                                key={module.id}
                                onClick={event => {
                                  event.stopPropagation();
                                  history.push(
                                    `/creator/modules/${module.id}`,
                                    {
                                      label: "Back to Anthologies",
                                      previousLocation: "/creator/anthologies",
                                    },
                                  );
                                }}
                              >
                                <Tooltip
                                  title={`Edit ${module.name}`}
                                  placement="bottom-start"
                                >
                                  <ListItemText
                                    primary={module.name}
                                    primaryTypographyProps={{
                                      noWrap: true,
                                    }}
                                  />
                                </Tooltip>

                                <ListItemSecondaryAction>
                                  <RemoveButton
                                    anthologyId={anthology.id}
                                    moduleId={module.id}
                                  />
                                </ListItemSecondaryAction>
                              </MenuItem>
                            ),
                            anthology.ancestorModules,
                          )}
                        </List>
                      </Popover>
                    )}
                  />
                </TableCell>

                <TableCell
                  key={"anthologyId:" + anthology.id}
                  align="center"
                  onClick={e => e.stopPropagation()}
                >
                  <CopyIcon value={anthology.id} text="Anthology ID" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10]}
        component="div"
        count={total}
        rowsPerPage={10}
        page={page}
        onChangePage={(event, page) => handleChangePage(page)}
      />
    </Paper>
  );
};

export default AnthologyTable;
